import React from 'react';
import { X, Calendar, Users, Shield, UserCheck, Building2, Mail, Phone } from 'lucide-react';
import Button from '../common/Button';
import Loading from '../common/Loading';

const DataViewModal = ({
  isOpen,
  onClose,
  title,
  data,
  isLoading,
  error,
  type // 'events', 'users', 'attendees', 'event_admins'
}) => {
  if (!isOpen) return null;

  const renderEventItem = (event) => (
    <div key={event.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Calendar className="h-5 w-5 text-blue-600" />
        </div>
        <div>
          <h4 className="font-medium text-gray-900 truncate" title={event.name}>{event.name}</h4>
          <p className="text-sm text-gray-500">
            {event.attendee_count || 0} attendees • Created {new Date(event.created_at).toLocaleDateString()}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className={`px-2 py-1 text-xs rounded-full ${event.is_active
            ? 'bg-green-100 text-green-800'
            : 'bg-gray-100 text-gray-800'
          }`}>
          {event.is_active ? 'Active' : 'Inactive'}
        </span>
      </div>
    </div>
  );

  const renderUserItem = (user) => (
    <div key={user.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${user.role === 'super_admin' ? 'bg-red-100' :
            user.role === 'event_admin' ? 'bg-purple-100' : 'bg-gray-100'
          }`}>
          {user.role === 'super_admin' ? <Shield className="h-5 w-5 text-red-600" /> :
            user.role === 'event_admin' ? <UserCheck className="h-5 w-5 text-purple-600" /> :
              <Users className="h-5 w-5 text-gray-600" />}
        </div>
        <div>
          <h4 className="font-medium text-gray-900">{user.full_name}</h4>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Mail className="h-3 w-3" />
              {user.email}
            </span>
            <span className="capitalize">{user.role.replace('_', ' ')}</span>
          </div>
        </div>
      </div>
      <div className="text-xs text-gray-400">
        Joined {new Date(user.created_at).toLocaleDateString()}
      </div>
    </div>
  );

  const renderAttendeeItem = (attendee) => (
    <div key={attendee.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-green-100 rounded-lg">
          <Users className="h-5 w-5 text-green-600" />
        </div>
        <div>
          <h4 className="font-medium text-gray-900">{attendee.full_name}</h4>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Mail className="h-3 w-3" />
              {attendee.email}
            </span>
            {attendee.phone && (
              <span className="flex items-center gap-1">
                <Phone className="h-3 w-3" />
                {attendee.phone}
              </span>
            )}
          </div>
          {attendee.organization && (
            <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
              <Building2 className="h-3 w-3" />
              {attendee.organization}
            </p>
          )}
        </div>
      </div>
      <div className="text-right">
        <div className="flex items-center gap-2 mb-1">
          <span className={`px-2 py-1 text-xs rounded-full ${attendee.check_in_status === 'checked_in'
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-800'
            }`}>
            {attendee.check_in_status === 'checked_in' ? 'Checked In' : 'Pending'}
          </span>
        </div>
        <p className="text-xs text-gray-400 truncate" title={attendee.event?.name}>{attendee.event?.name}</p>
      </div>
    </div>
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loading text="Loading data..." />
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-12">
          <div className="text-red-600 mb-2">
            <X className="h-8 w-8 mx-auto" />
          </div>
          <p className="text-gray-600">Failed to load data</p>
          <p className="text-sm text-gray-500 mt-1">{error.message}</p>
        </div>
      );
    }

    if (!data || data.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-2">
            <Users className="h-8 w-8 mx-auto" />
          </div>
          <p className="text-gray-600">No data found</p>
        </div>
      );
    }

    return (
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {data.map(item => {
          switch (type) {
            case 'events':
              return renderEventItem(item);
            case 'users':
            case 'event_admins':
              return renderUserItem(item);
            case 'attendees':
              return renderAttendeeItem(item);
            default:
              return null;
          }
        })}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 truncate pr-4" title={title}>{title}</h3>
              <p className="text-sm text-gray-500 mt-1">
                {isLoading ? 'Loading...' : `${data?.length || 0} items`}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {renderContent()}
          </div>

          {/* Footer */}
          <div className="flex justify-end p-6 border-t bg-gray-50">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataViewModal;
